// Copyright (c) 2026 CTO's Club LLC. All rights reserved.

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import {
    AnchorbaseLoginDeepLinkTokenSchema,
    type AnchorbaseLoginDeepLinkToken,
    type LoginResponse,
} from '@anchorbase/pillar-sdk';
import { ApiError, authApi } from '@/lib/pillarApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AnchorbaseLogo } from '@/components/branding/AnchorbaseLogo';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import { buildRequiredTotpSetupPath } from '@/lib/requiredTotpSetup';
import { sessionSensitiveAuthApi } from '@/lib/sessionSensitiveAuthApi';
import { invalidateSessionSensitiveRequests } from '@/lib/sessionSensitiveRequestManager';
import {
    appendCloverOAuthLoginContext,
    buildCloverOAuthLaunchPath,
    parseCloverOAuthLoginContext,
} from '@/lib/clover/oauth-launch-context';
import { useAuthStore } from '@/store/auth';
import { runInBackground } from '@/lib/utils';
import { PASSWORDLESS_LOGIN_EMAIL_STORAGE_KEY } from '../passwordless-login-context';
import {
    appendNativeAppLoginContinuation,
    buildPasswordChangePath,
    NATIVE_APP_LOGIN_PATH,
} from '@/lib/native-app-login-continuation';

const PasswordlessCodeFormSchema = z.object({
    code: z.string().regex(/^\d{6}$/, 'Enter the six digit code.'),
});

type PasswordlessCodeFormData = z.infer<typeof PasswordlessCodeFormSchema>;

type VerifierSubmission =
    | { readonly kind: 'code'; readonly code: string }
    | { readonly kind: 'deep_link'; readonly value: AnchorbaseLoginDeepLinkToken };

interface PasswordlessError {
    readonly title: string;
    readonly body: string;
}

export default function PasswordlessLoginPage() {
    const log = createComponentLogger('PasswordlessLoginPage');
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const queryClient = useQueryClient();
    const { logout, setUserForEffectiveSessionGeneration, getEffectiveSessionGeneration } =
        useAuthStore();
    const [error, setError] = useState<PasswordlessError | null>(null);
    const linkExchangeStarted = useRef(false);
    const email = window.sessionStorage.getItem(PASSWORDLESS_LOGIN_EMAIL_STORAGE_KEY);
    const paymentRequestUuid = searchParams.get('payment_request_uuid')?.trim() ?? '';
    const returnToPayment = searchParams.get('return_to_payment') === '1';
    const cloverOAuthLaunchContext = parseCloverOAuthLoginContext(searchParams);
    const resolvePostLoginPath = (nativeAppLoginContinuation: boolean): string =>
        cloverOAuthLaunchContext === null
            ? returnToPayment
                ? '/pay'
                : nativeAppLoginContinuation
                  ? NATIVE_APP_LOGIN_PATH
                  : '/dashboard'
            : buildCloverOAuthLaunchPath(cloverOAuthLaunchContext);
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<PasswordlessCodeFormData>({
        resolver: zodResolver(PasswordlessCodeFormSchema),
        defaultValues: { code: '' },
    });

    const verifierMutation = useMutation({
        mutationFn: async (submission: VerifierSubmission): Promise<LoginResponse> => {
            switch (submission.kind) {
                case 'code':
                    return await authApi.verifyPasswordlessLoginCode({ code: submission.code });
                case 'deep_link':
                    switch (submission.value.token_kind) {
                        case 'passwordless_login':
                            return await authApi.exchangePasswordlessLoginLink({
                                token: submission.value.token,
                            });
                        case 'pillar_login_link':
                            return await authApi.swapTokenForCookie({
                                token: submission.value.token,
                            });
                    }
            }
        },
        onSuccess: async (data, submission) => {
            const nativeAppLoginContinuation =
                submission.kind === 'deep_link' &&
                submission.value.token_kind === 'passwordless_login';
            const continueToNativeApp =
                nativeAppLoginContinuation &&
                cloverOAuthLaunchContext === null &&
                !returnToPayment;
            window.sessionStorage.removeItem(PASSWORDLESS_LOGIN_EMAIL_STORAGE_KEY);
            queryClient.clear();
            logout();
            invalidateSessionSensitiveRequests();
            const nextSearchParams = new URLSearchParams();
            if (paymentRequestUuid.length > 0) {
                nextSearchParams.set('payment_request_uuid', paymentRequestUuid);
            }
            if (returnToPayment) {
                nextSearchParams.set('return_to_payment', '1');
            }
            appendNativeAppLoginContinuation(
                nextSearchParams,
                continueToNativeApp
            );
            appendCloverOAuthLoginContext(nextSearchParams, cloverOAuthLaunchContext);

            if (data.requires_mfa_setup || data.requires_totp_setup) {
                const target =
                    data.mfa_setup_activation_target ?? data.totp_setup_activation_target;
                const setupUrl = new URL(
                    buildRequiredTotpSetupPath(target),
                    window.location.origin
                );
                for (const [key, value] of nextSearchParams.entries()) {
                    setupUrl.searchParams.set(key, value);
                }
                await navigate(`${setupUrl.pathname}${setupUrl.search}`, { replace: true });
                return;
            }
            if (data.requires_mfa || data.requires_totp) {
                const mfaMethods =
                    data.available_mfa_methods.length > 0
                        ? data.available_mfa_methods
                        : data.requires_totp
                          ? ['TOTP']
                          : [];
                if (mfaMethods.length > 0) {
                    nextSearchParams.set('mfa_methods', mfaMethods.join(','));
                }
                const query = nextSearchParams.toString();
                await navigate(`/login/totp${query.length > 0 ? `?${query}` : ''}`, {
                    replace: true,
                });
                return;
            }

            try {
                const profile = await sessionSensitiveAuthApi.getProfile();
                if (profile !== null) {
                    setUserForEffectiveSessionGeneration(profile, getEffectiveSessionGeneration());
                }
            } catch (profileError) {
                log.error('Failed to fetch profile after passwordless login', profileError);
            }
            window.location.assign(
                data.requires_password_change
                    ? buildPasswordChangePath(continueToNativeApp)
                    : resolvePostLoginPath(continueToNativeApp)
            );
        },
        onError: (mutationError: Error) => {
            if (mutationError instanceof ApiError && mutationError.status === 410) {
                setError({
                    title: 'That code expired',
                    body: 'Sign-in codes expire after 10 minutes. Send yourself a new code to continue.',
                });
            } else if (mutationError instanceof ApiError && mutationError.status === 429) {
                setError({
                    title: 'Try again in a few minutes',
                    body: 'There were too many sign-in attempts. Wait a few minutes and request a new code.',
                });
            } else {
                setError({
                    title: "That code didn't work",
                    body: 'Check the code and try again, or request a new one.',
                });
            }
            log.error('Passwordless verifier rejected', mutationError);
        },
    });
    const submitVerifier = verifierMutation.mutate;

    useEffect(() => {
        const exchangeFragment = () => {
            if (linkExchangeStarted.current || window.location.hash.length <= 1) {
                return;
            }
            linkExchangeStarted.current = true;
            const token = window.location.hash.slice(1);
            window.history.replaceState(
                window.history.state,
                '',
                `${window.location.pathname}${window.location.search}`
            );
            const parsedToken = AnchorbaseLoginDeepLinkTokenSchema.safeParse(token);
            if (!parsedToken.success) {
                setError({
                    title: "That sign-in link can't be used",
                    body: 'Request a new sign-in email and open its link again.',
                });
                return;
            }
            submitVerifier({ kind: 'deep_link', value: parsedToken.data });
        };
        exchangeFragment();
        window.addEventListener('hashchange', exchangeFragment);
        return () => window.removeEventListener('hashchange', exchangeFragment);
    }, [submitVerifier]);

    const resend = () => {
        const query = searchParams.toString();
        runInBackground(
            navigate(`/login${query.length > 0 ? `?${query}` : ''}`, { replace: true }),
            (navigationError) => {
                log.error('Failed to return to passwordless email entry', navigationError);
            }
        );
    };

    const submitCode = (data: PasswordlessCodeFormData) => {
        setError(null);
        submitVerifier({ kind: 'code', code: data.code });
    };

    const exchangingLink = linkExchangeStarted.current && verifierMutation.isPending;
    if (exchangingLink) {
        return (
            <div className="flex min-h-screen items-center justify-center p-4 bg-gradient-to-br from-blue-50 to-purple-50">
                <Card className="w-full max-w-md">
                    <CardHeader>
                        <CardTitle>Signing you in</CardTitle>
                        <CardDescription>One moment while we open Anchorbase.</CardDescription>
                    </CardHeader>
                </Card>
            </div>
        );
    }

    return (
        <div className="flex min-h-screen items-center justify-center p-4 bg-gradient-to-br from-blue-50 to-purple-50">
            <Card className="w-full max-w-md">
                <CardHeader className="space-y-0">
                    <div className="mb-6 flex justify-center">
                        <AnchorbaseLogo width={196} />
                    </div>
                    <div className="space-y-2">
                        <CardTitle>Check your email</CardTitle>
                        <CardDescription>
                            We sent a six digit code to {email ?? 'your work email'}.
                        </CardDescription>
                    </div>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit(submitCode)} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="passwordless-code">Sign-in code</Label>
                            <Input
                                id="passwordless-code"
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                maxLength={6}
                                {...register('code')}
                                disabled={verifierMutation.isPending}
                            />
                            {errors.code !== undefined && (
                                <p className="text-sm text-destructive">{errors.code.message}</p>
                            )}
                            <p className="text-sm text-muted-foreground">
                                The code expires in 10 minutes.
                            </p>
                        </div>

                        {error !== null && (
                            <div
                                role="alert"
                                className="rounded-md bg-destructive/15 p-3 text-sm text-destructive"
                            >
                                <div className="font-medium">{error.title}</div>
                                <div>{error.body}</div>
                            </div>
                        )}

                        <Button
                            type="submit"
                            className="w-full"
                            disabled={verifierMutation.isPending}
                        >
                            {verifierMutation.isPending
                                ? 'Signing in...'
                                : error === null
                                  ? 'Sign in'
                                  : 'Try again'}
                        </Button>
                        <Button type="button" variant="link" className="w-full" onClick={resend}>
                            Send a new code
                        </Button>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
}
